/**
 * useT - Hook para traducir strings en componentes
 *
 * @returns {Function} t(key, params) → string traducido o "t???key"
 *
 * @example
 * import { useT } from '@/i18n';
 *
 * function MyComponent() {
 *   const t = useT();
 *
 *   return (
 *     <div>
 *       <button>{t('common.save')}</button>
 *       <span>{t('game.turn', { turn: 5 })}</span>
 *     </div>
 *   );
 * }
 */

import { useCallback } from 'react';
import useTranslationStore from '@/store/useTranslationStore';

export default function useT() {
  // Subscribe to primitive values only (not functions) - triggers re-render on locale change
  const translations = useTranslationStore((state) => state.translations);
  const locale = useTranslationStore((state) => state.locale);

  // Create t function locally, memoized based on translations
  const t = useCallback((key, params = {}) => {
    let text = translations[key];

    if (!text) {
      console.warn(`[i18n-${locale.toUpperCase()}] Missing translation: ${key}`);
      return `t???${key}`;
    }

    // Interpolation: {name} → params.name
    if (params && Object.keys(params).length > 0) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), String(value));
      });
    }

    return text;
  }, [translations, locale]);

  return t;
}
